import { createContext, useCallback, useContext, useMemo, useState } from "react";

const CollapseContext = createContext({
    isCollapsed: () => false,
    togglePath: () => {},
});

/**
 * Renders JSON-style punctuation (colon) with shared styling.
 * @param {{ children: import("react").ReactNode }} props
 * @returns {import("react").ReactElement}
 */
const Punct = ({ children }) => {
    return <span className="account-tree-punct">{children}</span>;
};

/**
 * One account’s body: labeled department strings and optional nested sub-accounts.
 * @param {object} props
 * @param {{ departments: string[], subAccounts?: Record<string, unknown> }} props.node
 * @param {string} props.accountPath Path from root for nested collapse keys (e.g. `ABC/EFG`).
 * @param {number} props.indent Left padding in pixels for this block.
 * @returns {import("react").ReactElement}
 */
const AccountObjectBody = ({ node, accountPath, indent }) => {
    const hasSubs = node.subAccounts && Object.keys(node.subAccounts).length > 0;
    const entries = hasSubs ? Object.entries(node.subAccounts) : [];

    return (
        <div className="account-tree-block" style={{ paddingLeft: indent }}>
            <div className="account-tree-line">
                <span className="account-tree-prop-key">
                    {node.departments.length}{" "}
                    {node.departments.length === 1 ? "Department" : "Departments"}
                </span>
                <Punct>:</Punct>
            </div>
            {node.departments.map((d, i) => (
                <div key={i} className="account-tree-line account-tree-dept-line">
                    <span className="account-tree-string">&quot;{d}&quot;</span>
                </div>
            ))}
            {hasSubs && (
                <>
                    <div className="account-tree-line">
                        <span className="account-tree-prop-key">
                            {entries.length}{" "}
                            {entries.length === 1 ? "Sub Account" : "Sub Accounts"}
                        </span>
                        <Punct>:</Punct>
                    </div>
                    <div style={{ paddingLeft: 12 }}>
                        {entries.map(([childName, childNode]) => (
                            <div key={childName} className="account-tree-sub-entry">
                                <AccountNamedBlock
                                    name={childName}
                                    node={childNode}
                                    accountPath={`${accountPath}/${childName}`}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

/**
 * Collapsible account header and its departments / sub-accounts body.
 * @param {object} props
 * @param {string} props.name Account display name (quoted in the UI).
 * @param {{ departments: string[], subAccounts?: Record<string, unknown> }} props.node
 * @param {string} props.accountPath Unique path for collapse state (root: `Name`; nested: `A/B/C`).
 * @returns {import("react").ReactElement}
 */
const AccountNamedBlock = ({ name, node, accountPath }) => {
    const { isCollapsed, togglePath } = useContext(CollapseContext);
    const collapsed = isCollapsed(accountPath);

    return (
        <div className="account-tree-named">
            <div
                className="account-tree-line account-tree-account-header"
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                onClick={() => togglePath(accountPath)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        togglePath(accountPath);
                    }
                }}
            >
                <span className="account-tree-chevron" aria-hidden>
                    {collapsed ? "▶" : "▼"}
                </span>
                <span className="account-tree-account-key">&quot;{name}&quot;</span>
            </div>
            {collapsed ? (
                <div className="account-tree-line account-tree-collapsed">
                    <span className="account-tree-ellipsis">...</span>
                </div>
            ) : (
                <>
                    <AccountObjectBody node={node} accountPath={accountPath} indent={12} />
                </>
            )}
        </div>
    );
};

/**
 * Tree preview for the parsed account/department hierarchy: styled labels, counts, collapsible account rows.
 * @param {{
 *   tree: Record<string, { departments: string[], subAccounts?: Record<string, unknown> }> | null | undefined,
 * }} props
 * @returns {import("react").ReactElement}
 */
const AccountTreePreview = ({ tree }) => {
    const [collapsedPaths, setCollapsedPaths] = useState(() => new Set());

    const togglePath = useCallback((path) => {
        setCollapsedPaths((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
        });
    }, []);

    const isCollapsed = useCallback((path) => collapsedPaths.has(path), [collapsedPaths]);

    const ctx = useMemo(() => ({ togglePath, isCollapsed }), [togglePath, isCollapsed]);

    const rootEntries = Object.entries(tree ?? {});

    return (
        <CollapseContext.Provider value={ctx}>
            <div className="account-tree-preview">
                <div style={{ paddingLeft: 0 }}>
                    {rootEntries.map(([name, node]) => (
                        <div key={name} className="account-tree-root-entry">
                            <AccountNamedBlock name={name} node={node} accountPath={name} />
                        </div>
                    ))}
                </div>
            </div>
        </CollapseContext.Provider>
    );
};

export default AccountTreePreview;
